import { call, put, select, takeLatest } from "redux-saga/effects";
import { godModeApi } from "../../firebase-api/godMode";
import { loginApi } from "../../firebase-api/login";
import { removeItem, setItem } from "../../libs/encryptedStorage";
import {
    clearGodModeSession,
    getGodModeSession,
    setGodModeSession,
} from "../../util/godMode";
import Helper from "../../util/helper";
import { notification } from "../../util/notification";
import actions from "../actionType";

export function* fetchStatus() {
    try {
        const response = yield call(godModeApi.fetchStatus);
        const data = response?.body?.data || {};
        yield put({
            type: actions.SET_GOD_MODE_STATUS,
            payload: {
                isOperator: !!data.isOperator,
                operator: data.operator || null,
                sessionTtlMinutes: data.sessionTtlMinutes,
            },
        });
    } catch (err) {
        // A 403 here just means "not an operator" — the common case for every
        // real client user, so it must not surface as an error.
        yield put({
            type: actions.SET_GOD_MODE_STATUS,
            payload: { isOperator: false, operator: null },
        });
    }
}

export function* fetchCenters(request) {
    try {
        const { search, limit } = request.payload || {};
        const response = yield call(godModeApi.fetchCenters, search, limit);
        yield put({
            type: actions.SET_GOD_MODE_CENTERS,
            payload: { centers: response?.body?.data?.centers || [] },
        });
    } catch (err) {
        Helper.notifyBugsnag(err, true);
        yield put({
            type: actions.GOD_MODE_FAILED,
            payload: { error: "Failed to load centers" },
        });
    }
}

/**
 * Open a session and hand off to the ordinary login flow.
 *
 * The server returns the same { schoolConfig, teacher } pair the normal
 * profile picker works with, so from here on God Mode reuses the existing login
 * path — including signing in to the school's secondary database. The teacher
 * carries `godMode: true`, which the login saga uses to skip staff-only side
 * effects such as writing lastLogin (that write would create a /teachers row and
 * put the operator in the client's staff list).
 */
export function* startSession(request) {
    try {
        const { centerId, reason } = request.payload;
        // The picker is reachable with a session still open (back button, typed
        // URL). Close it first: otherwise the old server session stays live, its
        // school's Firebase app stays signed in, and its header rides along on the
        // startSession call itself.
        const previous = getGodModeSession() || (yield select((state: any) => state.godMode?.session));
        if (previous) {
            yield call(teardownClientSession, previous, "Replaced by a new God Mode session", false);
        }
        const response = yield call(godModeApi.startSession, centerId, reason);
        const data = response?.body?.data;
        if (!data?.session || !data?.schoolConfig || !data?.teacher) {
            throw new Error("Malformed God Mode session response");
        }

        setGodModeSession(data.session);

        // The school database sign-in is minted per session by GET /settings/dbToken
        // during the ordinary login flow, so the config carries no credentials.
        const schoolConfig = data.schoolConfig;

        const teacher = { ...data.teacher, centerId: data.session.centerId };

        setItem("user", teacher);
        setItem("schoolConfig", schoolConfig);

        yield put({
            type: actions.GOD_MODE_SESSION_STARTED,
            payload: { session: data.session },
        });
        yield put({
            type: actions.CONTINUE_WITH_NEW_USER,
            payload: { newUser: teacher, schoolConfig },
        });
    } catch (err) {
        Helper.notifyBugsnag(err, true);
        clearGodModeSession();
        const message =
            err?.response?.body?.message || "Failed to start God Mode session";
        notification("error", message);
        yield put({ type: actions.GOD_MODE_FAILED, payload: { error: message } });
    }
}

export function* elevateSession(request) {
    try {
        const session = getGodModeSession();
        if (!session) throw new Error("No active God Mode session");

        const response = yield call(
            godModeApi.elevateSession,
            session.sessionId,
            request.payload.reason,
        );
        const updated = response?.body?.data?.session;
        const nextSession = { ...session, mode: updated?.mode || "WRITE" };
        setGodModeSession(nextSession);

        yield put({
            type: actions.GOD_MODE_SESSION_ELEVATED,
            payload: { session: nextSession },
        });
        notification("success", "Write mode enabled. Every change is being recorded.");
    } catch (err) {
        Helper.notifyBugsnag(err, true);
        const message =
            err?.response?.body?.message || "Failed to enable write mode";
        notification("error", message);
        yield put({ type: actions.GOD_MODE_FAILED, payload: { error: message } });
    }
}

/**
 * Tear down the client-scoped half of the session, leaving the operator signed in.
 *
 * The Auth0 and master Firebase sessions belong to the operator, not to the client
 * they were just inside, so a switch must not touch them — otherwise moving between
 * two clients means two full logins. What does have to go is everything scoped to
 * the center: the server-side God Mode session, the school's Firebase auth session,
 * the cached center data in local storage, and the open realtime channels.
 *
 * `loggedInEncodedEmail` deliberately survives: the picker reads it on mount to
 * fetch the operator's own profiles.
 *
 * `routeToPicker` is off when the caller is already on its way somewhere else —
 * opening another session, or entering the operator's own account.
 */
export function* teardownClientSession(session: any, reason: string, routeToPicker = true) {
    // Clear the stale SIGN_SUCCESS first. The picker routes straight back to the
    // dashboard while it is still set, which would bounce the operator into the
    // client they are trying to leave.
    yield put({ type: actions.RESET_OPERATION_TYPE_LOGIN, payload: null });

    try {
        if (session) {
            yield call(godModeApi.endSession, session.sessionId, reason);
        }
    } catch (err) {
        // The session expires on its own regardless, so a failed end call must not
        // trap the operator inside the client's account.
        console.log("[godMode] Failed to end session cleanly", err);
    }

    clearGodModeSession();

    // Stop the realtime listeners before dropping the credentials underneath them.
    yield put({ type: actions.CLOSE_CHANNELS, payload: null });

    if (session?.centerId) {
        yield call(loginApi.signOutSecondaryDb, session.centerId);
    }

    // Only the center-scoped keys — see the note above about loggedInEncodedEmail.
    ["user", "schoolConfig", "schoolName", "teacher", "teacherList", "student", "userList", "accountList"]
        .forEach((key) => removeItem(key));

    // Navigation happens off the back of this, and deliberately not before: the
    // picker fires its own authenticated calls on mount, and authHelper.getAuthToken
    // builds a fresh Auth0Client per call. One client de-duplicates concurrent
    // silent-token requests; separate instances each try to redeem the same
    // rotating refresh token, and the losers come back "Login required" — which
    // getAuthToken handles by logging the user out entirely. Finishing the one
    // call this saga makes before the picker starts its own keeps them serialised.
    if (routeToPicker) {
        yield put({ type: actions.GOD_MODE_SWITCH_READY, payload: {} });
    } else {
        yield put({ type: actions.SET_GOD_MODE_SESSION_STATE, payload: { session: null } });
    }
}

export function* switchClient(request) {
    // Storage drops a session the moment it expires locally, so fall back to the
    // store — teardown still needs the centerId to sign out of the school's
    // database, and an expired session is exactly when that matters most.
    const session = getGodModeSession() || (yield select((state: any) => state.godMode?.session));
    yield call(
        teardownClientSession,
        session,
        request.payload?.reason || "Switched to another client",
    );
}

/** Re-reads the server's view of the session — catches expiry and a mode change
 *  made from another tab. */
export function* refreshSession() {
    try {
        const session = getGodModeSession() || (yield select((state: any) => state.godMode?.session));
        if (!session) return;
        const response = yield call(godModeApi.fetchSessionStatus, session.sessionId);
        const data = response?.body?.data;
        if (!data?.valid) {
            // Expired or revoked: drop back to the picker, same as an explicit
            // switch. The operator's own login is still perfectly good.
            notification("info", data?.reason || "God Mode session has ended");
            yield call(teardownClientSession, session, data?.reason || "Session no longer valid");
            return;
        }
        const nextSession = {
            ...session,
            mode: data.session.mode,
            expiresOn: data.session.expiresOn,
        };
        setGodModeSession(nextSession);
        yield put({
            type: actions.SET_GOD_MODE_SESSION_STATE,
            payload: { session: nextSession },
        });
    } catch (err) {
        console.log("[godMode] Failed to refresh session", err);
    }
}

export default function* godModeSaga() {
    yield takeLatest(actions.FETCH_GOD_MODE_STATUS, fetchStatus);
    yield takeLatest(actions.FETCH_GOD_MODE_CENTERS, fetchCenters);
    yield takeLatest(actions.START_GOD_MODE_SESSION, startSession);
    yield takeLatest(actions.ELEVATE_GOD_MODE_SESSION, elevateSession);
    yield takeLatest(actions.SWITCH_GOD_MODE_CLIENT, switchClient);
    yield takeLatest(actions.REFRESH_GOD_MODE_SESSION, refreshSession);
}
